import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { useEffect } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    leftPadding = "leftPadding",
    topPadding = "topPadding",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.leftPadding]: number;
        [fields.topPadding]: number;
    };
}
interface Props extends OwnProps {}

export interface ProductLineDetailBannerStyles {
    container?: InjectableCss;
    readMoreLink?: InjectableCss;
    hideContent?: InjectableCss;
    showContent?: InjectableCss;
    lessContent?: InjectableCss;
    readMoreLinkWrapper?: InjectableCss;
}

export const bannerStyles: ProductLineDetailBannerStyles = {
    container: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) => breakpointMediaQueries(theme, [null], "max")}
        `,
    },
    showContent: {
        css: css`
            height: auto;
            transition: all 0.4s ease;
            body {
                background: transparent;
            }
        `,
    },
    hideContent: {
        css: css`
            height: 111px;
            transition: all 0.4s ease;
            overflow: hidden;
            body {
                background: transparent;
            }
        `,
    },
    lessContent: {
        css: css`
            height: auto;
            transition: all 0.4s ease;
            body {
                background: transparent;
            }
        `,
    },
    readMoreLinkWrapper: {
        css: css`
            color: #ef3742;
            display: flex;
            justify-content: flex-start;
            margin-top: 5px;
            margin-bottom: 15px;
        `,
    },
    readMoreLink: {
        css: css`
            color: #ef3742;
            &:hover {
                text-decoration: underline;
            }
        `,
    },
};

const styles = bannerStyles;

export interface ProductLineDetailBannerStyles {
    container?: InjectableCss;
    readMoreLink?: InjectableCss;
    hideContent?: InjectableCss;
    showContent?: InjectableCss;
    lessContent?: InjectableCss;
    readMoreLinkWrapper?: InjectableCss;
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        productsDataView: getProductListDataView(state),
    };
};

type props = WidgetProps & ReturnType<typeof mapStateToProps>;

const ProductLineDescription = (props: any) => {
    const [isReady, setIsReady] = React.useState(false);
    const [readMore, setReadMore] = React.useState(false);
    const [currentDeviceWidth, setCurrentDeviceWidth] = React.useState(1366);
    useEffect(() => {
        setIsReady(false);
        const timer = setTimeout(() => {
            setIsReady(true);
        }, 1000);

        return () => clearTimeout(timer);
    }, [props.productsDataView]);

    useEffect(() => {
        setCurrentDeviceWidth(window.screen.width);
        const handleResize = () => {
            setCurrentDeviceWidth(window.screen.width);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    });

    if (!isReady) {
        return null;
    }

    if (typeof window !== "undefined") {
        const params = new URLSearchParams(window.location.search);
        const productLineIds = params.get("productLineIds");

        if (productLineIds) {
            return null;
        }
    }
    const description = props.productsDataView.properties?.productLineDescription;
    function extractBodyContent(html: string) {
        const match = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
        return match ? match[1] : html;
    }
    if (!description) {
        return null;
    }
    const stringDescription = JSON.stringify(description);
    if (!stringDescription) {
        return null;
    }
    const DivStyle = styled.div`
        padding-left: ${fields.leftPadding}px;
        padding-top: ${fields.topPadding}px;
    `;

    const isCount =
        currentDeviceWidth > 1024 // desktop
            ? stringDescription.length > 920
            : currentDeviceWidth === 1024 // Ipad pro
              ? stringDescription.length > 820
              : currentDeviceWidth >= 768 && currentDeviceWidth < 1024 // ipad
                ? stringDescription.length > 640
                : currentDeviceWidth >= 425 && currentDeviceWidth < 768 // iphoneX
                  ? stringDescription.length > 440
                  : currentDeviceWidth >= 375 && currentDeviceWidth < 425 // iphone 6/7/8 Plus
                    ? stringDescription.length > 340
                    : currentDeviceWidth >= 320 && currentDeviceWidth < 375 // iphone 5/6/7/8
                      ? stringDescription.length > 340
                      : currentDeviceWidth >= 0 && currentDeviceWidth < 320 // lower than iphone 5 Devices
                        ? stringDescription.length > 240
                        : false;
    return (
        <StyledWrapper {...styles.container}>
            <DivStyle>
                <StyledWrapper
                    {...(isCount
                        ? readMore
                            ? { ...styles.showContent }
                            : { ...styles.hideContent }
                        : { ...styles.lessContent })}
                    id="categoryDesc"
                >
                    {parse(extractBodyContent(description), parserOptions)}
                </StyledWrapper>
                <StyledWrapper {...styles.readMoreLinkWrapper}>
                    <Clickable
                        {...styles.readMoreLink}
                        key="readMore"
                        onClick={() => setReadMore(!readMore)}
                        data-test-selector="readMoreContent"
                    >
                        {isCount ? (readMore ? "Read Less" : "Read More") : ""}
                    </Clickable>
                </StyledWrapper>
            </DivStyle>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ProductLineDescription),
    definition: {
        displayName: "ProductLineDescription",
        group: "Product List",
        fieldDefinitions: [
            {
                name: fields.leftPadding,
                displayName: "Left Padding",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 100,
                min: 0,
            },
            {
                name: fields.topPadding,
                displayName: "Top Padding",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 100,
                min: 0,
            },
        ],
    },
};
export default widgetModule;
